import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  capitalizationOptions,
  turnoverRangeOptions,
  totalAssetsRangeOptions,
  numberOfEmployeesRangeOptions,
  balanceSheetRangeOptions,
  industryOptions,
  companyTypeOptions,
  sizeOptions,
  mainCategoryOptions as originalMainCategoryOptions,
  lawTypeOptions
} from "../../data/data";
import { isEmpty } from "lodash";
import StyledLabel from "../shared/StyledLabel";
import StyledInput from "../shared/StyledInput";

import TextArea from "antd/es/input/TextArea";
import { DatePicker, Input, Select, Checkbox } from "antd";
import { Upload } from "..";

import { ReactComponent as Calendar20x20Icon } from "../../assets/calendar20x20.svg";

import { createLaw } from "../../store/laws/actions";
import { sendEmail } from "../../store/email/actions";
import { sortOptions } from "../../util/sorters";
import { createLawMessage } from "../../util/emailTemplates";

const LawForm = () => {
  const dispatchito = useDispatch();
  // create a ref for the file input
  const inputRef = useRef(null);

  //form references
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  //set focus on title field
  const focusTitle = () => {
    titleRef.current.focus();
  };

  const user = useSelector(state => state.user);
  const organizations = useSelector(state => state.organizations.organizations);
  const organizationOptions = !isEmpty(organizations)
    ? organizations.map(org => {
        return { value: org._id, label: org.customerName };
      })
    : [];
  // console.debug("*****************************user:", user);
  // console.debug("organizationOptions",organizationOptions);

  const CheckboxGroup = Checkbox.Group;

  const [countLawType, setCountLawType] = useState(uuidv4());
  const [countMainCategory, setCountMainCategory] = useState(uuidv4());

  //form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [inForceDate, setInForceDate] = useState(null);
  // const [dueDate, setDueDate] = useState(null);
  const [applyToAll, setApplyToAll] = useState(true);
  const [applyToSpecificOrgs, setApplyToSpecificOrgs] = useState(false);
  const [isListed, setIsListed] = useState(false);
  const [isNotListed, setIsNotListed] = useState(false);
  const [isBothListedAndNotListed, setIsBothListedAndNotListed] =
    useState(true);
  const [industries, setIndustries] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [size, setSize] = useState([]);
  const [type, setType] = useState(null);
  const [numberOfEmployees, setNumberOfEmployees] = useState([]);
  const [capitalization, setCapitalization] = useState([]);
  const [turnover, setTurnover] = useState([]);
  const [totalAssets, setTotalAssets] = useState([]);
  const [balanceSheet, setBalanceSheet] = useState([]);

  //control fields (not submitted in form)
  const [applyCapitalization, setApplyCapitalization] = useState(false);
  const [applyTurnover, setApplyTurnover] = useState(false);
  const [applyTotalAssets, setApplyTotalAssets] = useState(false);
  const [applyNumberOfEmployees, setApplyNumberOfEmployees] = useState(false);
  const [applyBalanceSheet, setApplyBalanceSheet] = useState(false);

  //show error for checkboxes
  const [errorCapitalization, setErrorCapitalization] = useState(false);
  const [errorTurnover, setErrorTurnover] = useState(false);
  const [errorTotalAssets, setErrorTotalAssets] = useState(false);
  const [errorNumOfEmployees, setErrorNumOfEmployees] = useState(false);
  const [errorBalanceSheet, setErrorBalanceSheet] = useState(false);
  const errorMessage = "please choose at least one option";

  //empty fields
  const [isTitleEmpty, setIsTitleEmpty] = useState(true);
  const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(true);

  //disabled fields
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);

  //error message
  const [error, setError] = useState(null);

  const [emptyFields, setEmptyFields] = useState([]);

  const [mainCategory, setMainCategory] = useState(null);

  const [disableSubmit, setDisableSubmit] = useState(true);

  //file related state
  const [singleFile, setSingleFile] = useState([]);
  const [singleFileName, setSingleFileName] = useState("");

  //file related handlers
  const handleFileChange = ({ files }) => {
    const chosenFiles = files.file;

    setSingleFile(chosenFiles);
    setSingleFileName([chosenFiles][0].name);
    setIsSubmitButtonDisabled(false);
  };

  const removeFile = ({ files }) => {
    setSingleFile([])
    setSingleFileName("");
    setIsSubmitButtonDisabled(true);
  }

  const clearFields = () => {
    //reset form fields
    setIndustries([]);
    setSelectedOrganizations([]);
    setTitle("");
    setInForceDate(null);
    setApplyToAll(true);
    setApplyToSpecificOrgs(false);
    setIsTitleEmpty(true);
    setIsDescriptionEmpty(true);
    setDescription("");
    setMainCategory(null);
    setType(null);
    setIsListed(false);
    setIsNotListed(false);
    setIsBothListedAndNotListed(true);
    setNumberOfEmployees([]);
    setCapitalization([]);
    setTurnover([]);
    setTotalAssets([]);
    setBalanceSheet([]);
    setSingleFileName("");
    setIsSubmitButtonDisabled(true);
    setError(null);
    setEmptyFields([]);
    setSingleFile([]);
    setCompanyTypes([]);
    setSize([]);
    //reset form control fields
    setApplyCapitalization(false);
    setApplyTurnover(false);
    setApplyTotalAssets(false);
    setApplyNumberOfEmployees(false);
    setApplyBalanceSheet(false);
  };

  useEffect(() => {
    //check for other required fields besides document
    if (
      title &&
      description &&
      inForceDate &&
      type &&
      mainCategory &&
      (!applyToSpecificOrgs || !isEmpty(selectedOrganizations))
    ) {
      setDisableSubmit(false);
    } else {
      setDisableSubmit(true);
    }
  }, [
    title,
    description,
    inForceDate,
    type,
    mainCategory,
    applyToSpecificOrgs,
    selectedOrganizations
  ]);

  const checkboxErrors = () => {
    let errors = false;
    if (applyBalanceSheet && !balanceSheet.length) {
      errors = true;
      setErrorBalanceSheet(true);
    }
    if (applyNumberOfEmployees && !numberOfEmployees.length) {
      errors = true;
      setErrorNumOfEmployees(true);
    }
    if (applyTotalAssets && !totalAssets.length) {
      errors = true;
      setErrorTotalAssets(true);
    }
    if (applyTurnover && !turnover.length) {
      errors = true;
      setErrorTurnover(true);
    }
    if (applyCapitalization && !capitalization.length) {
      errors = true;
      setErrorCapitalization(true);
    }
    return errors;
  };

  const onInForceDateChange = (date, dateString) => {
    // console.debug("in force date:", date);
    // console.debug("in force dateString:", dateString);
    setInForceDate(date);
  };

  //options for radio buttons code here
  const onApplyToAllOptionChange = e => {
    setApplyToAll(e.target.value === "all" ? true : false);
    setApplyToSpecificOrgs(e.target.value === "orgs" ? true : false);
    //reset fields appropriately
    if (e.target.value === "all" || e.target.value === "orgs") {
      //empty filters
      setSelectedOrganizations([]);
      setIndustries([]);
      setNumberOfEmployees([]);
      setCapitalization([]);
      setTurnover([]);
      setTotalAssets([]);
      setBalanceSheet([]);
      setCompanyTypes([]);
      setSize([]);
      //reset form control fields
      setApplyCapitalization(false);
      setApplyTurnover(false);
      setApplyTotalAssets(false);
      setApplyNumberOfEmployees(false);
      setApplyBalanceSheet(false);
    }
    if (e.target.value === "all" || e.target.value === "filters") {
      setSelectedOrganizations([]);
    }
  };

  const onListedOptionChange = e => {
    switch (e.target.id) {
      case "listed":
        setIsListed(true);
        setIsNotListed(false);
        setIsBothListedAndNotListed(false);
        break;
      case "notListed":
        setIsListed(false);
        setIsNotListed(true);
        setIsBothListedAndNotListed(false);
        // when changed to non listed clear capitalization range options
        setApplyCapitalization(false);
        setCapitalization([]);
        break;
      case "bothListedAndNotListed":
        setIsListed(false);
        setIsNotListed(false);
        setIsBothListedAndNotListed(true);
        break;
      default:
        break;
    }
  };

  //checkboxes handlers
  const handleCapitalizationChange = selectedOption => {
    setCapitalization(selectedOption.map(option => option));
    if (applyCapitalization) {
      //if no options
      setErrorCapitalization(!selectedOption.length ? true : false);
    }
    // console.debug(`Capitalization Options selected:`, selectedOption);
  };

  const handleTurnoverChange = selectedOption => {
    setTurnover(selectedOption.map(option => option));
    if (applyTurnover) {
      //if no options
      setErrorTurnover(!selectedOption.length ? true : false);
    }
    // console.debug(`Turnover Options selected:`, selectedOption);
  };

  const handleTotalAssetsChange = selectedOption => {
    setTotalAssets(selectedOption.map(option => option));
    if (applyTotalAssets) {
      //if no options
      setErrorTotalAssets(!selectedOption.length ? true : false);
    }
    // console.debug(`TotalAssets Options selected:`, selectedOption);
  };

  const handleBalanceSheetChange = selectedOption => {
    setBalanceSheet(selectedOption.map(option => option));
    if (applyBalanceSheet) {
      //if no options
      setErrorBalanceSheet(!selectedOption.length ? true : false);
    }
    // console.debug(`BalanceSheet Options selected:`, selectedOption);
  };

  const handleNumberOfEmployeesChange = selectedOption => {
    setNumberOfEmployees(selectedOption.map(option => option));
    if (applyNumberOfEmployees) {
      //if no options
      setErrorNumOfEmployees(!selectedOption.length ? true : false);
    }
    // console.debug(`NumberOfEmployees Options selected:`, selectedOption);
  };

  const handleCheckboxChange = (
    selectedOption,
    applyCheck,
    setCheckboxes,
    error
  ) => {
    applyCheck(selectedOption);
    setCheckboxes([]);
    error(false);
  };

  //select handlers
  const handleMainCategoryChange = selectedOption => {
    setMainCategory(selectedOption);
    // console.debug(`Main Category Option selected:`, selectedOption);
  };

  const handleTypeChange = selectedOption => {
    setType(selectedOption);
    // console.debug(`Type Option selected:`, selectedOption, type);
  };

  const handleIndustriesChange = selectedOption => {
    //create an array of strings from selectedOption array
    const industriesArray = selectedOption.map(option => option);
    setIndustries(industriesArray);
    // console.debug("industriesArray: ", industriesArray);
  };

  const handleOrganizationsChange = selectedOption => {
    //create an array of strings from selectedOption array
    const organizationsArray = selectedOption.map(option => option);
    setSelectedOrganizations(organizationsArray);
    // console.debug("industriesArray: ", industriesArray);
  };

  const handleSizeChange = selectedOption => {
    //create an array of strings from selectedOption array
    // console.debug("SIZE selected", selectedOption);
    const sizesArray = selectedOption.map(option => option);
    // console.debug("SIZE mapped", sizesArray);
    setSize(sizesArray);
    // console.debug("sizesArray: ", sizesArray);
    //setSize(selectedOption);
    //console.debug(`Size Option selected:`, selectedOption);
  };

  const customFilterOption = (option, inputValue) => {
    return inputValue.label.toLowerCase().includes(option.toLowerCase());
  };

  const handleCompanyTypeChange = selectedOption => {
    //create an array of strings from selectedOption array
    const typesArray = selectedOption.map(option => option);
    setCompanyTypes(typesArray);
  };

  //handlers
  const handleSubmit = async e => {
    e.preventDefault();
    setDisableSubmit(true);

    const formData = new FormData();

    if (!user) {
      setError("You need to be logged in to add a law");
      return;
    }

    formData.get("pdfDocument");

    let errorCheck = checkboxErrors();
    if (errorCheck) {
      setError("Missing options");
      setDisableSubmit(false);
      return;
    }

    const law = {
      type,
      title,
      inForceDate,
      applyToAll,
      applyToSpecificOrgs,
      description,
      mainCategory,
      // dueDate,
      isListed,
      isNotListed,
      isBothListedAndNotListed,
      //industries: reference.current,
      selectedOrganizations,
      industries,
      companyTypes,
      size,
      numberOfEmployees,
      capitalization,
      turnover,
      totalAssets,
      balanceSheet
    };
    let i;
    // console.debug("law.isListed", law.isListed);
    formData.append("title", law.title);
    formData.append("inForceDate", law.inForceDate);
    formData.append("applyToAll", law.applyToAll);
    formData.append("applySpecificallyToOrgs", law.applyToSpecificOrgs);
    formData.append("description", law.description);
    formData.append("mainCategory", law.mainCategory);
    formData.append("type", law.type);
    // formData.append("dueDate", law.dueDate);
    formData.append("isListed", law.isListed);
    formData.append("isNotListed", law.isNotListed);
    formData.append("isBothListedAndNotListed", law.isBothListedAndNotListed);
    // law.industries.forEach((str, index) => {
    //   formData.append(`string_${index}`, str);
    // });

    //append fields of multiple values
    if (law.industries.length) {
      for (i = 0; i < law.industries.length; i++) {
        formData.append("industries[]", law.industries[i]);
      }
    } else {
      formData.append("industries", law.industries);
    }
    if (law.selectedOrganizations.length) {
      for (i = 0; i < law.selectedOrganizations.length; i++) {
        formData.append("organizations[]", law.selectedOrganizations[i]);
      }
    } else {
      formData.append("organizations", law.selectedOrganizations);
    }
    if (law.size.length) {
      for (i = 0; i < law.size.length; i++) {
        formData.append("size[]", law.size[i]);
      }
    } else {
      formData.append("size", law.size);
    }
    if (law.turnover.length) {
      for (i = 0; i < law.turnover.length; i++) {
        formData.append("turnover[]", law.turnover[i]);
      }
    } else {
      formData.append("turnover", law.turnover);
    }
    if (law.totalAssets.length) {
      for (i = 0; i < law.totalAssets.length; i++) {
        formData.append("totalAssets[]", law.totalAssets[i]);
      }
    } else {
      formData.append("totalAssets", law.totalAssets);
    }
    if (law.numberOfEmployees.length) {
      for (i = 0; i < law.numberOfEmployees.length; i++) {
        formData.append("numberOfEmployees[]", law.numberOfEmployees[i]);
      }
    } else {
      formData.append("numberOfEmployees", law.numberOfEmployees);
    }
    if (law.companyTypes.length) {
      for (i = 0; i < law.companyTypes.length; i++) {
        formData.append("companyTypes[]", law.companyTypes[i]);
      }
    } else {
      formData.append("companyTypes", law.companyTypes);
    }
    if (law.capitalization.length) {
      for (i = 0; i < law.capitalization.length; i++) {
        formData.append("capitalization[]", law.capitalization[i]);
      }
    } else {
      formData.append("capitalization", law.capitalization);
    }
    if (law.balanceSheet.length) {
      for (i = 0; i < law.balanceSheet.length; i++) {
        formData.append("balanceSheet[]", law.balanceSheet[i]);
      }
    } else {
      formData.append("balanceSheet", law.balanceSheet);
    }
    /**new attribute to differenciate from manual upload and mass upload
     *always false when called from frontend*/
    formData.append("massUpload", false);

    formData.append("pdfDocument", singleFile, encodeURI(singleFile.name));

    dispatchito(
      createLaw({
        user,
        payload: formData,
        callback: (recipients, clear) => {
          if (clear) clearFields();
          // if (isEmpty(recipients) || !Array.isArray(recipients)) return;
          // for (const recipient of recipients) {
          //   // const userAssigned = usersData.find(item => item._id === user);

          //   dispatchito(
          //     sendEmail({
          //       user,
          //       // to: [userAssigned?.email],
          //       to: [recipient?.email],
          //       cc: [],
          //       subject: `${
          //         recipient?.company ? recipient?.company + " - " : ""
          //       }New Law Created`,
          //       message: createLawMessage(law, recipient),
          //       isHtmlMessage: true
          //       // message: 'HELLO text',
          //       // isHtmlMessage: false,
          //     })
          //   );
          // }
        }
      })
    );

    // if (!response.ok) {
    //   // console.debug("error area reached, (json.error):", json.error);
    //   setError(json.error);
    //   setEmptyFields(json.emptyFields);
    //   setDisableSubmit(false);
    // }
    // if (response.ok) {
    //reference.current.value = null;

    // console.debug("new law added", json);
    // dispatch({ type: "CREATE_LAW", payload: json });
    // triggerNotification({
    //   message: "Creation was successful",
    //   description: "The legal text was created successfully"
    // });
    // }
  };

  // let footer = <p>Please pick a day.</p>;
  // if (inForceDate) {
  //   footer = <p>You picked {format(inForceDate, "PP")}.</p>;
  // }

  const selectstyle = {
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: state.isSelected ? "#f5edff" : "white",
      color: "black",
      "&:hover": {
        backgroundColor: "#F9F5FF"
      }
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderRadius: "0.5rem",
      borderColor: state.isFocused || state.isSelected ? "#f5edff" : "#D9D9D9",
      outline: "none",
      boxShadow:
        state.isFocused || state.isSelected ? "0 0 0 0.125rem #f5edff" : "none",
      fontFamily: `'Roboto', sans-serif`,
      "&:hover": {
        borderColor: "#baa1de"
      }
    })
  };

  //set categories options based on who is logged in
  // if (user.organization === "") {
  //   mainCategoryOptions = mainCategoryOptions.filter(
  //     item => item.value !== "Other"
  //   );
  // }
  let mainCategoryOptions =
    user.organization === ""
      ? originalMainCategoryOptions?.filter(item => item.value !== "Other")
      : [{ value: "Other", label: "Other" }];

  return (
    <form className="create" onSubmit={handleSubmit}>
      <h3 style={{ marginBlockEnd: "1.25rem" }}>Add new Law</h3>
      <section>
        <div id="law-title">
          <StyledLabel required forHtml="law-title-input">
            Law Title:
          </StyledLabel>
          <Input
            id="law-title-input"
            style={{
              width: "100%",
              height: "2.75rem",
              marginBottom: "1.25rem"
            }}
            // autoFocus
            type="text"
            ref={titleRef}
            onChange={e => setTitle(e.target.value)}
            // onBlur={handleTitleBlur}
            value={title}
            className={emptyFields?.includes("title") ? "error" : ""}
            placeholder={`${
              isTitleEmpty ? "Law Title" : "Law Title is required"
            }`}
            size="large"
          />
        </div>

        <div id="law-description">
          <StyledLabel required forHtml="law-description-area">
            Law Description:
          </StyledLabel>
          <TextArea
            id="law-dercsription-area"
            rows="5"
            // autoSize={{ minRows: 2, maxRows: 6 }}
            ref={descriptionRef}
            onChange={e => setDescription(e.target.value)}
            // onBlur={handleDescriptionBlur}
            value={description}
            className={emptyFields?.includes("description") ? "error" : ""}
            placeholder={`${
              isDescriptionEmpty
                ? "Law Description"
                : "Law Description is required"
            }`}
            size="large"
            style={{ marginBottom: "1.25rem" }}
          />
        </div>
      </section>
      <section>
        <div id="select-law-type" style={{ marginTop: 0 }}>
          <StyledLabel
            required
            forHtml="law-type-select"
            style={{ marginBottom: "0.625rem" }}
          >
            Law Type:
          </StyledLabel>
          <Select
            virtual={false}
            // dropdownStyle={{ maxHeight: "17rem" }}
            style={{ width: "100%", marginBottom: "1.063rem" }}
            id="law-type-select"
            key={countLawType}
            options={lawTypeOptions}
            onChange={handleTypeChange}
            className="react-select-container"
            value={type}
            size="large"
            placeholder="Select"
          />
        </div>

        <div id="in-force-date" style={{ marginTop: 0 }}>
          <StyledLabel
            required
            forHtml="law-in-force-date"
            style={{ marginBottom: "0.625rem" }}
          >
            In Force Date:
          </StyledLabel>
          <DatePicker
            id="law-in-force-date"
            style={{
              width: "100%",
              height: "2.75rem",
              marginBottom: "1.125rem"
            }}
            value={inForceDate}
            onChange={onInForceDateChange}
            size="large"
            suffixIcon={<Calendar20x20Icon />}
            placeholder="Select"
            format="DD/MM/YYYY"
          />
        </div>
      </section>
      <section>
        <div id="select-framework" style={{ marginTop: 0 }}>
          <StyledLabel
            required
            forHtml="framework-select"
            style={{ marginBottom: "0.625rem" }}
          >
            Framework:
          </StyledLabel>
          <Select
            virtual={false}
            style={{ width: "100%", marginBottom: "1.25rem" }}
            id="framework-select"
            key={countMainCategory}
            options={mainCategoryOptions}
            onChange={handleMainCategoryChange}
            styles={selectstyle}
            className="react-select-container"
            value={mainCategory}
            size="large"
            placeholder="Select"
          />
        </div>
        {user.role !== "corporateEditor" && (
          <div id="option-group-listed">
            <StyledLabel style={{ marginBottom: "0.625rem" }}>
              Select which companies the law is applied to:
            </StyledLabel>
            <div className="radio-duo">
              <StyledLabel htmlFor="listed">Listed</StyledLabel>
              <StyledInput
                id="listed"
                type="radio"
                name="listedCompaniesGroup"
                // onChange={e => setApplyToAll(!applyToAll)}
                value="listed"
                checked={isListed}
                className={emptyFields?.includes("listed") ? "error" : ""}
                onChange={onListedOptionChange}
              />
            </div>

            <div className="radio-duo">
              <StyledLabel htmlFor="notListed">Not listed</StyledLabel>
              <StyledInput
                id="notListed"
                type="radio"
                name="listedCompaniesGroup"
                // onChange={e => setApplyToAll(!applyToAll)}
                value="notListed"
                checked={isNotListed}
                className={emptyFields?.includes("listed") ? "error" : ""}
                onChange={onListedOptionChange}
              />
            </div>

            <div className="radio-duo">
              <StyledLabel htmlFor="bothListedAndNotListed">Both</StyledLabel>
              <StyledInput
                id="bothListedAndNotListed"
                type="radio"
                name="listedCompaniesGroup"
                // onChange={e => setApplyToAll(!applyToAll)}
                value="both"
                checked={isBothListedAndNotListed}
                className={emptyFields?.includes("listed") ? "error" : ""}
                onChange={onListedOptionChange}
              />
            </div>
          </div>
        )}
        {/* hide this option for editors that aren't master */}
        {user && user?.role === "masterEditor" && (
          <>
            <StyledLabel style={{ marginTop: "1.25rem" }}>
              Select if the law applies to all industries or not:
            </StyledLabel>

            <div className="radio-duo-first">
              <StyledInput
                id="applyToAll"
                type="radio"
                name="applyToAllIndustries"
                // onChange={e => setApplyToAll(!applyToAll)}
                value="all"
                checked={applyToAll === true}
                className={emptyFields?.includes("applyToAll") ? "error" : ""}
                onChange={onApplyToAllOptionChange}
              />
              <StyledLabel htmlFor="applyToAll" style={{ marginBottom: 0 }}>
                Applies To All Industries
              </StyledLabel>
            </div>

            <div className="radio-duo-first">
              <StyledInput
                id="doesNotApplyToAll"
                type="radio"
                name="applyToAllIndustries"
                // onChange={e => setApplyToAll(!applyToAll)}
                value="filters"
                checked={applyToAll === false && applyToSpecificOrgs === false}
                className={emptyFields?.includes("applyToAll") ? "error" : ""}
                onChange={onApplyToAllOptionChange}
              />
              <StyledLabel
                htmlFor="doesNotApplyToAll"
                style={{ marginBottom: 0 }}
              >
                Applies To Specific Industries
              </StyledLabel>
            </div>

            <div className="radio-duo-first">
              <StyledInput
                id="applyToOrgs"
                type="radio"
                name="applyToAllIndustries"
                // onChange={e => setApplyToAll(!applyToAll)}
                value="orgs"
                checked={applyToSpecificOrgs}
                className={emptyFields?.includes("applyToAll") ? "error" : ""}
                onChange={onApplyToAllOptionChange}
              />
              <StyledLabel htmlFor="applyToOrgs">
                Applies To Specific Organizations
              </StyledLabel>
            </div>
          </>
        )}
        {applyToSpecificOrgs && (
          <Select
            style={{ width: "100%", marginBottom: "1.25rem" }}
            id="organizations-select"
            styles={{
              menu: (baseStyles, state) => ({
                ...baseStyles,
                fontFamily: "Roboto, sans-serif"
              }),
              option: (baseStyles, state) => ({
                ...baseStyles,
                backgroundColor: state.isSelected ? "#f5edff" : "white",
                "&:hover": {
                  backgroundColor: "#F9F5FF"
                }
              }),
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor:
                  state.isFocused || state.isSelected ? "#f5edff" : "#D0D5DD",
                outline: state.isFocused ? "0.063rem solid #725798" : "none",
                fontFamily: `'Roboto', sans-serif`,
                "&:hover": {
                  borderColor: "#baa1de"
                }
              })
            }}
            mode="multiple"
            name="organizations"
            value={selectedOrganizations}
            options={organizationOptions}
            filterOption={customFilterOption}
            className="basic-multi-select"
            onChange={handleOrganizationsChange}
            size="large"
            placeholder="Select"
          />
        )}
        {!applyToAll && !applyToSpecificOrgs && (
          <>
            <Select
              virtual={false}
              style={{ width: "100%", marginBottom: "1.25rem" }}

              id="industries-select"
              styles={{
                menu: (baseStyles, state) => ({
                  ...baseStyles,
                  fontFamily: "Roboto, sans-serif"
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isSelected ? "#f5edff" : "white",
                  "&:hover": {
                    backgroundColor: "#F9F5FF"
                  }
                }),
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor:
                    state.isFocused || state.isSelected ? "#f5edff" : "#D0D5DD",
                  outline: state.isFocused ? "0.063rem solid #725798" : "none",
                  fontFamily: `'Roboto', sans-serif`,
                  "&:hover": {
                    borderColor: "#baa1de"
                  }
                })
              }}
              mode="multiple"
              name="industries"
              value={industries}
              options={sortOptions(industryOptions)}
              className="basic-multi-select"
              onChange={handleIndustriesChange}
              size="large"
              placeholder="Select"
            />

            <StyledLabel htmlFor="company-type-select">
              Type of company the law is applied to:
            </StyledLabel>
            <Select
              virtual={false}
              style={{ width: "100%", marginBottom: "1.25rem" }}
              id="company-size-select"
              name="companyType"
              value={companyTypes}
              mode="multiple"
              options={companyTypeOptions}
              styles={selectstyle}
              className="basic-multi-select"
              onChange={handleCompanyTypeChange}
              size="large"
              placeholder={companyTypes.length > 0 ? "Select" : "Select"}
              filterOption={customFilterOption}
            />

            <StyledLabel
              htmlFor="company-size-select"
              style={{ marginTop: "1.25rem" }}
            >
              Size of the company the law is applied to:
            </StyledLabel>
            <Select
              virtual={false}
              style={{ width: "100% " }}
              id="company-size-select"
              name="size"
              mode="multiple"
              options={sizeOptions}
              styles={selectstyle}
              className="basic-multi-select"
              value={size}
              onChange={handleSizeChange}
              size="large"
              placeholder="Select"
            />

            {isListed && (
              <div className="checkboxes-group">
                <Checkbox
                  id="capitalization-range-check"
                  className="single-check"
                  value={applyCapitalization}
                  onChange={e =>
                    handleCheckboxChange(
                      e.target.checked,
                      setApplyCapitalization,
                      setCapitalization,
                      setErrorCapitalization
                    )
                  }
                >
                  The law applies to companies with specific range (or ranges)
                  of Capitalization.
                </Checkbox>
                {applyCapitalization && (
                  <>
                    <span
                      key="cap-error"
                      className="checkboxes-error-message"
                      style={{
                        visibility: errorCapitalization ? "visible" : "hidden"
                      }}
                    >
                      {errorMessage}
                    </span>
                    <CheckboxGroup
                      id="capitalization-range-checkboxes"
                      className="range-checkboxes"
                      options={capitalizationOptions}
                      value={capitalization}
                      onChange={handleCapitalizationChange}
                    />
                  </>
                )}
              </div>
            )}

            <div className="checkboxes-group">
              <Checkbox
                id="turnover-range-check"
                className="single-check"
                value={applyTurnover}
                onChange={e =>
                  handleCheckboxChange(
                    e.target.checked,
                    setApplyTurnover,
                    setTurnover,
                    setErrorTurnover
                  )
                }
              >
                The law applies to companies with specific range (or ranges) of
                Turnover.
              </Checkbox>
              {applyTurnover && (
                <>
                  <span
                    className="checkboxes-error-message"
                    style={{ visibility: errorTurnover ? "visible" : "hidden" }}
                  >
                    {errorMessage}
                  </span>
                  <CheckboxGroup
                    id="turnover-range-checkboxes"
                    className="range-checkboxes"
                    options={turnoverRangeOptions}
                    value={turnover}
                    onChange={handleTurnoverChange}
                  />
                </>
              )}
            </div>

            <div className="checkboxes-group">
              <Checkbox
                id="total-assets-range-check"
                className="single-check"
                value={applyTotalAssets}
                onChange={e =>
                  handleCheckboxChange(
                    e.target.checked,
                    setApplyTotalAssets,
                    setTotalAssets,
                    setErrorTotalAssets
                  )
                }
              >
                The law applies to companies with specific range (or ranges) of
                Total Assets.
              </Checkbox>
              {applyTotalAssets && (
                <>
                  <span
                    className="checkboxes-error-message"
                    style={{
                      visibility: errorTotalAssets ? "visible" : "hidden"
                    }}
                  >
                    {errorMessage}
                  </span>
                  <CheckboxGroup
                    id="total-assets-range-checkboxes"
                    className="range-checkboxes"
                    options={totalAssetsRangeOptions}
                    value={totalAssets}
                    onChange={handleTotalAssetsChange}
                  />
                </>
              )}
            </div>

            <div className="checkboxes-group">
              <Checkbox
                id="number-of-employees-range-check"
                className="single-check"
                options={applyNumberOfEmployees}
                onChange={e =>
                  handleCheckboxChange(
                    e.target.checked,
                    setApplyNumberOfEmployees,
                    setNumberOfEmployees,
                    setErrorNumOfEmployees
                  )
                }
              >
                The law applies to companies with specific range (or ranges) of
                number of Employees.
              </Checkbox>
              {applyNumberOfEmployees && (
                <>
                  <span
                    className="checkboxes-error-message"
                    style={{
                      visibility: errorNumOfEmployees ? "visible" : "hidden"
                    }}
                  >
                    {errorMessage}
                  </span>
                  <CheckboxGroup
                    id="number-of-employees-range-checkboxes"
                    className="range-checkboxes"
                    options={numberOfEmployeesRangeOptions}
                    value={numberOfEmployees}
                    onChange={handleNumberOfEmployeesChange}
                  />
                </>
              )}
            </div>

            <div className="checkboxes-group">
              <Checkbox
                id="balance-sheet-range-range-check"
                className="single-check"
                value={applyBalanceSheet}
                onChange={e =>
                  handleCheckboxChange(
                    e.target.checked,
                    setApplyBalanceSheet,
                    setBalanceSheet,
                    setErrorBalanceSheet
                  )
                }
              >
                The law applies to companies with specific range (or ranges) of
                Balance Sheet.
              </Checkbox>
              {applyBalanceSheet && (
                <>
                  <span
                    className="checkboxes-error-message"
                    style={{
                      visibility: errorBalanceSheet ? "visible" : "hidden"
                    }}
                  >
                    {errorMessage}
                  </span>
                  <CheckboxGroup
                    id="balance-sheet-range-checkboxes"
                    className="range-checkboxes"
                    options={balanceSheetRangeOptions}
                    value={balanceSheet}
                    onChange={handleBalanceSheetChange}
                  />
                </>
              )}
            </div>
          </>
        )}
      </section>

      <hr />
      <StyledLabel
        required
        htmlFor="file-upload"
        className="required-field"
        style={{ marginTop: "1.25rem", color: "#344054" }}
      >
        Supporting Documents:
      </StyledLabel>
      <Upload
        id="file-upload"
        item={{
          fileCode: "law",
          required: true,
          fileAccepts: ".pdf",
          fileLimit: 30,
          files: singleFile,
          fileName: singleFileName,
          multiple: false
        }}
        onRemove={removeFile}
        alreadyUploaded={!isSubmitButtonDisabled}
        handleFiles={handleFileChange}
        setSingleFile={setSingleFile}
        setIsSubmitButtonDisabled={setIsSubmitButtonDisabled}
        group="law"
        accept={".pdf"}
        hasTitle={false}
      />

      <button
        style={{ marginTop: 0, width: "19.688rem" }}
        id="submitButton"
        disabled={isSubmitButtonDisabled || disableSubmit}
        onClick={() => {
          setCountLawType(uuidv4());
          setCountMainCategory(uuidv4());
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }}
      >
        {/* {!isSubmitButtonDisabled ? "Add Law" : "Add Law (disabled)"} */}
        Add Law
      </button>
      {error && <div className="error">{error}</div>}
    </form>
  );
};

export default LawForm;
